import { NumberEntry } from '../types/angelNumber';
import { allNumbers, getNumber, normalizeKey } from '../data/numbers';
import { categories, typeMap } from '../data/categories';

export type MatchReason = 'exact' | 'related' | 'keyword';

export interface SearchResult {
  entry: NumberEntry;
  reason: MatchReason;
}

/** Numbers written with spaces, colons, hyphens, dots or slashes. */
const NUMERIC_QUERY = /^[\s\d:.\-/|]+$/;

/** "11:11", "11 11", "1-1-1-1" all collapse to "1111". */
export function toDigits(query: string): string {
  return query.replace(/\D/g, '');
}

export function isNumericQuery(query: string): boolean {
  const q = query.trim();
  return q.length > 0 && NUMERIC_QUERY.test(q) && toDigits(q).length > 0;
}

/** The single best match for a query, if there is an unambiguous one. */
export function resolveExact(query: string): NumberEntry | undefined {
  const q = query.trim();
  if (!q) return undefined;

  const direct = getNumber(q);
  if (direct) return direct;

  if (isNumericQuery(q)) {
    const digits = toDigits(q);
    // Long runs of one digit (e.g. 1111111) fall back to the deepest entry.
    if (/^(\d)\1+$/.test(digits)) {
      const d = digits[0];
      for (let len = Math.min(digits.length, 5); len >= 2; len -= 1) {
        const match = getNumber(d.repeat(len));
        if (match) return match;
      }
    }
  }
  return undefined;
}

function keywordScore(entry: NumberEntry, q: string): number {
  let score = 0;
  const title = entry.title.toLowerCase();
  if (title.includes(q)) score += 60;
  if (title.startsWith(q)) score += 20;

  entry.keywords.forEach((keyword) => {
    const key = keyword.toLowerCase();
    if (key === q) score += 50;
    else if (key.includes(q) || q.includes(key)) score += 26;
  });

  entry.searchAliases.forEach((alias) => {
    if (normalizeKey(alias) === normalizeKey(q)) score += 70;
  });

  categories
    .filter((category) => category.label.toLowerCase().includes(q))
    .forEach((category) => {
      if (entry.categories.includes(category.id)) score += 34;
    });

  if (entry.categories.some((category) => category.replace(/_/g, ' ').includes(q))) score += 30;

  const typeLabel = typeMap[entry.type].label.toLowerCase();
  if (typeLabel.includes(q)) score += 24;

  if (entry.shortMeaning.toLowerCase().includes(q)) score += 12;
  if (entry.coreMeaning.content.toLowerCase().includes(q)) score += 6;

  // Prefer the shorter, more familiar sequences when scores are close.
  score += Math.max(0, 6 - entry.number.length);
  score += Math.round(entry.popularityScore / 12);
  return score;
}

export function searchNumbers(query: string, limit = 30): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  if (isNumericQuery(q)) {
    const digits = toDigits(q);
    const results: SearchResult[] = [];
    const seen = new Set<string>();

    const exact = resolveExact(q);
    if (exact) {
      results.push({ entry: exact, reason: 'exact' });
      seen.add(exact.id);
    }

    allNumbers
      .filter((entry) => !seen.has(entry.id) && entry.number.startsWith(digits))
      .sort((x, y) => y.popularityScore - x.popularityScore)
      .slice(0, limit)
      .forEach((entry) => {
        results.push({ entry, reason: 'related' });
        seen.add(entry.id);
      });

    allNumbers
      .filter((entry) => !seen.has(entry.id) && entry.number.includes(digits))
      .slice(0, limit)
      .forEach((entry) => {
        results.push({ entry, reason: 'related' });
        seen.add(entry.id);
      });

    return results.slice(0, limit);
  }

  return allNumbers
    .map((entry) => ({ entry, score: keywordScore(entry, q) }))
    .filter((result) => result.score > 28)
    .sort((x, y) => y.score - x.score)
    .slice(0, limit)
    .map((result) => ({ entry: result.entry, reason: 'keyword' as MatchReason }));
}

export const searchSuggestions = [
  'portal',
  'love',
  'abundance',
  'protection',
  'manifestation',
  'twin flame',
  'spiritual growth',
  'transformation',
];
